/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef, useSyncExternalStore } from 'react';
import axios from '../api/axios';
import { useAuth } from './AuthContext';
import { filterNavigationApps, parseAppsNavigation, type AppsNavigationDecision } from './apps-navigation.policy';
import { getCentralAppsRegistryUrl } from '../utils/coreAssetUrl';
import { getCompanyScopeSnapshot, registerCompanyScopeInvalidator } from '../utils/company-scope-lifecycle.utils';

export interface AppModuleInfo {
  code: string;
  name: string;
  description: string;
  tooltipDescription?: string;
  icon: string;
  /** `GET /api/apps` ile gelen tam SVG; UI gerekirse kullanır. */
  iconSvg?: string;
  colorLight: string;
  colorDark: string;
  path: string;
  displayOrder: number;
  label?: Record<string, string>;
  descriptionLabel?: Record<string, string>;
  tooltipDescriptionLabel?: Record<string, string>;
}

export interface AppInfo {
  code: string;
  name: string;
  description: string;
  tooltipDescription?: string;
  icon: string;
  /** `GET /api/apps` ile gelen tam SVG. */
  iconSvg?: string;
  colorLight: string;
  colorDark: string;
  path: string;
  displayOrder: number;
  label?: Record<string, string>;
  descriptionLabel?: Record<string, string>;
  tooltipDescriptionLabel?: Record<string, string>;
  modules?: AppModuleInfo[];
  /**
   * Modül satırı için ana uygulama kodu (örn. ERP alt modülü → orderParentCode: ERP).
   * UserSetting appsOrder üst kodları içerdiğinde AppsGrid eşlemesi için kullanılır.
   */
  orderParentCode?: string;
}

/** Merkezi apps kaydında seçili dilde görünen ad (sidebar, tooltip, sekme başlığı). */
export function pickLocalizedAppLabel(app: AppInfo | undefined, lang: string): string | undefined {
  if (!app) return undefined;
  const key = String(lang ?? '')
    .toLowerCase()
    .trim()
    .split('-')[0];
  const fromLabel =
    app.label?.[lang] ?? app.label?.[key] ?? (typeof app.label?.en === 'string' ? app.label.en : undefined);
  const base = (typeof fromLabel === 'string' && fromLabel.trim() ? fromLabel : app.name)?.trim();
  return base || undefined;
}

interface AppsContextValue {
  apps: AppInfo[];
  mainApps: AppInfo[];
  loading: boolean;
  getApp: (code: string) => AppInfo | undefined;
  getMainAppCode: (code: string) => string;
  getIconColor: (app: Pick<AppInfo, 'code' | 'colorLight' | 'colorDark'>, theme: string) => string;
}

const APPS_CONTEXT_GLOBAL_KEY = 'qubepro_apps_context';

function getStableAppsContext(): React.Context<AppsContextValue | undefined> {
  const g = globalThis as typeof globalThis & {
    [APPS_CONTEXT_GLOBAL_KEY]?: React.Context<AppsContextValue | undefined>;
  };
  if (!g[APPS_CONTEXT_GLOBAL_KEY]) {
    g[APPS_CONTEXT_GLOBAL_KEY] = createContext<AppsContextValue | undefined>(undefined);
  }
  return g[APPS_CONTEXT_GLOBAL_KEY]!;
}

const AppsContext = getStableAppsContext();


function trimBaseUrl(raw: string): string {
  return String(raw ?? '').trim().replace(/\/+$/, '');
}

function getAppsRegistryPath(): string {
  const explicit = trimBaseUrl(import.meta.env.VITE_APPS_REGISTRY_PATH as string);
  if (explicit) {
    return explicit;
  }
  return getCentralAppsRegistryUrl();
}

/** Uygulama listesi ürün-local `/api/apps` üstünden gelir; uydu backend Core proxy'sini server-side yapar. */
function isAppsRegistryPublicListPath(noQueryUrl: string): boolean {
  const n = stripBasePathFromUrl(noQueryUrl).replace(/\/+$/, '').toLowerCase();
  return n.endsWith('/apps');
}

function stripBasePathFromUrl(pathOrUrl: string): string {
  const noQuery = pathOrUrl.trim().split('?')[0] || '';
  if (/^https?:\/\//i.test(noQuery)) {
    try {
      const u = new URL(noQuery);
      return u.pathname || '';
    } catch {
      return noQuery;
    }
  }
  const b = (import.meta.env.VITE_BASE_PATH || '').replace(/\/+$/, '');
  let p = noQuery.startsWith('/') ? noQuery : `/${noQuery}`;
  if (b && p.startsWith(b)) {
    p = p.slice(b.length) || '/';
  }
  return p.startsWith('/') ? p : `/${p}`;
}

/** Kimlik gerekir mi? Uygulama listesi public kabul edilir; protected proxy uçları auth gate ister. */
function appsRegistryLikelyRequiresSession(path: string): boolean {
  const flag = import.meta.env.VITE_APPS_REGISTRY_REQUIRES_AUTH;
  if (flag === 'true' || flag === '1') return true;
  if (flag === 'false' || flag === '0') return false;

  const noQuery = path.trim().split('?')[0] || '';

  if (isAppsRegistryPublicListPath(noQuery)) return false;

  if (/^https?:\/\//i.test(noQuery)) {
    return false;
  }
  return stripBasePathFromUrl(noQuery).toLowerCase().startsWith('/api/core');
}

/** isAuthenticated anlık false olunca (geçici dalga) registry kapısı — uydu/session dalgalarına tolerans */
const APPS_AUTH_GATE_OFF_MS = 1600;

/** Ardışık registry cevapları arası minimum süre (efekt/remount fırtınası) */
const APPS_REGISTRY_REPEAT_SKIP_MS = 30000;
const APPS_REGISTRY_ERROR_SKIP_MS = 15000;

export function AppsProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isInitialized, user } = useAuth();
  const [catalogTree, setAppsTree] = useState<AppInfo[]>([]);
  const navigationScope = JSON.stringify([isAuthenticated, user?.id, user?.tenantId, user?.membershipId, user?.authVersion, user?.userKind, user?.permissions]);
  const registryAuthority = JSON.stringify([isAuthenticated, user?.id, user?.tenantId, user?.membershipId, user?.authVersion, user?.membershipVersion]);
  const companyScopeRevision = useSyncExternalStore(registerCompanyScopeInvalidator,
    () => getCompanyScopeSnapshot().revision, () => 0);
  const navigationRequestScope = JSON.stringify([navigationScope, companyScopeRevision]);
  const [navigation, setNavigation] = useState<{ scope: string; decision: AppsNavigationDecision | null }>({ scope: '', decision: null });
  useEffect(() => {
    let active = true;
    const scope = getCompanyScopeSnapshot();
    setNavigation({ scope: navigationRequestScope, decision: null });
    if (!scope.blocked && isAuthenticated && user?.userKind === 'EXTERNAL') {
      void axios.get('/api/apps/navigation').then(response => {
        if (active && !scope.signal.aborted && scope.revision === getCompanyScopeSnapshot().revision)
          setNavigation({ scope: navigationRequestScope, decision: parseAppsNavigation(response.data?.data ?? response.data) });
      }).catch(() => { /* Unavailable authority keeps the external launcher empty. */ });
    }
    return () => { active = false; };
  }, [navigationRequestScope]);
  const appsTree = useMemo(() => filterNavigationApps(catalogTree, isAuthenticated ? user?.userKind : 'INTERNAL',
    navigation.scope === navigationRequestScope ? navigation.decision : null), [catalogTree, isAuthenticated, user?.userKind, navigation, navigationRequestScope]);
  const [loading, setLoading] = useState(true);
  const [authGate, setAuthGate] = useState(isAuthenticated);
  const appsFetchSeqRef = useRef(0);
  const appsRegistryInflightRef = useRef<Map<string, Promise<unknown>>>(new Map());
  const appsRegistryOkUntilRef = useRef(0);
  const appsRegistryEverOkRef = useRef(false);
  const appsRegistryAuthorityRef = useRef(registryAuthority);

  const registryPath = useMemo(() => getAppsRegistryPath(), []);
  const needsAuth = useMemo(() => appsRegistryLikelyRequiresSession(registryPath), [registryPath]);
  /** needsAuth=false iken authGate dalgalanması liste efektini tetiklemesin (boolean sabit kalır). */
  const registryFetchBlocked = needsAuth ? !authGate : false;

  useEffect(() => {
    if (isAuthenticated) {
      setAuthGate(true);
      return;
    }
    const t = window.setTimeout(() => setAuthGate(false), APPS_AUTH_GATE_OFF_MS);
    return () => window.clearTimeout(t);
  }, [isAuthenticated]);

  useEffect(() => {
    const seq = ++appsFetchSeqRef.current;
    if (!isInitialized) {
      return;
    }

    if (appsRegistryAuthorityRef.current !== registryAuthority) {
      appsRegistryAuthorityRef.current = registryAuthority;
      appsRegistryEverOkRef.current = false;
      appsRegistryOkUntilRef.current = 0;
      setAppsTree([]);
    }

    const scope = getCompanyScopeSnapshot();
    if (registryFetchBlocked || scope.blocked) {
      appsRegistryInflightRef.current.clear();
      // Oturum anlık dalga: liste cache + cooldown sıfırlanmaz — gereksiz /api/apps yenilemesi kesilir
      setLoading(false);
      return;
    }

    const now = Date.now();
    if (appsRegistryEverOkRef.current && now < appsRegistryOkUntilRef.current) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // İlk firma doğrulaması aynı HTTP 200 yanıtını scope fence'te iptal edebilir.
    // Yalnız gerçek lifecycle değişimi yeni kapsam için tekrar yükler; eski promise
    // yeni actor/firma isteğine katılamaz. Sabit kapsam hatalarında polling yapılmaz.
    const requestKey = JSON.stringify([registryPath, registryAuthority, scope.revision]);
    const isCurrentRequest = () => seq === appsFetchSeqRef.current && !scope.signal.aborted &&
      scope.revision === getCompanyScopeSnapshot().revision;
    let loadPromise = appsRegistryInflightRef.current.get(requestKey);
    if (!loadPromise) {
      const req = axios.get(registryPath).finally(() => {
        appsRegistryInflightRef.current.delete(requestKey);
      });
      appsRegistryInflightRef.current.set(requestKey, req);
      loadPromise = req;
    }

    void Promise.resolve(loadPromise)
      .then((raw) => {
        if (!isCurrentRequest()) return;
        const res = raw as { data?: { data?: unknown } };
        const payload = res.data?.data ?? res.data;
        const validPayload = Array.isArray(payload) ? payload : [];
        setAppsTree(validPayload);
        appsRegistryEverOkRef.current = true;
        appsRegistryOkUntilRef.current = Date.now() + APPS_REGISTRY_REPEAT_SKIP_MS;
      })
      .catch(() => {
        if (!isCurrentRequest()) return;
        if (!appsRegistryEverOkRef.current) {
          setAppsTree([]);
        }
        appsRegistryOkUntilRef.current = Date.now() + APPS_REGISTRY_ERROR_SKIP_MS;
      })
      .finally(() => {
        if (isCurrentRequest()) {
          setLoading(false);
        }
      });
    return () => { if (seq === appsFetchSeqRef.current) appsFetchSeqRef.current += 1; };
  }, [registryPath, needsAuth, isInitialized, registryFetchBlocked, registryAuthority, companyScopeRevision]);

  const apps = useMemo(() => {
    const flat: AppInfo[] = [];
    for (const app of appsTree) {
      if (app.modules?.length) {
        const parentPath = (app.path || '').replace(/\/$/, '');
        for (const mod of app.modules) {
          const modPath = mod.path.startsWith('/') ? mod.path : `/${mod.path}`;
          flat.push({
            code: mod.code,
            name: mod.name,
            description: mod.description,
            ...(mod.tooltipDescription != null ? { tooltipDescription: mod.tooltipDescription } : {}),
            icon: mod.icon,
            ...(mod.iconSvg != null ? { iconSvg: mod.iconSvg } : {}),
            colorLight: mod.colorLight,
            colorDark: mod.colorDark,
            path: `${parentPath}${modPath}`,
            displayOrder: app.displayOrder * 1000 + mod.displayOrder,
            orderParentCode: app.code,
            ...(mod.label && { label: mod.label }),
            ...(mod.descriptionLabel && { descriptionLabel: mod.descriptionLabel }),
            ...(mod.tooltipDescriptionLabel && { tooltipDescriptionLabel: mod.tooltipDescriptionLabel }),
          });
        }
      } else {
        flat.push(app);
      }
    }
    flat.sort((a, b) => {
      const ao = Number(a.displayOrder);
      const bo = Number(b.displayOrder);
      const aok = Number.isFinite(ao) ? ao : Number.MAX_SAFE_INTEGER;
      const bok = Number.isFinite(bo) ? bo : Number.MAX_SAFE_INTEGER;
      if (aok !== bok) return aok - bok;
      return String(a.code || '').localeCompare(String(b.code || ''), undefined, { sensitivity: 'base' });
    });
    return flat;
  }, [appsTree]);

  const mainApps = useMemo(
    () => appsTree.filter((a) => a.code !== 'CORE'),
    [appsTree],
  );

  const getApp = useCallback(
    (code: string): AppInfo | undefined =>
      apps.find((a) => a.code === code) ?? appsTree.find((a) => a.code === code),
    [apps, appsTree],
  );

  const getMainAppCode = useCallback(
    (code: string): string => {
      const direct = appsTree.find((a) => a.code === code);
      if (direct) return code;
      const parent = appsTree.find((a) =>
        a.modules?.some((m) => m.code === code),
      );
      return parent ? parent.code : code;
    },
    [appsTree],
  );

  const getIconColor = useCallback(
    (app: Pick<AppInfo, 'code' | 'colorLight' | 'colorDark'>, theme: string): string =>
      theme === 'dark' ? app.colorDark : app.colorLight,
    [],
  );

  const ctxValue = useMemo(
    () => ({ apps, mainApps, loading, getApp, getMainAppCode, getIconColor }),
    [apps, mainApps, loading, getApp, getMainAppCode, getIconColor],
  );

  return (
    <AppsContext.Provider value={ctxValue}>
      {children}
    </AppsContext.Provider>
  );
}

export function useApps(): AppsContextValue {
  const ctx = useContext(AppsContext);
  if (!ctx) throw new Error('useApps() must be used within <AppsProvider>');
  return ctx;
}
